import { z } from "zod";
import { IsoTimestamp, MatchId, NightId, PlayerId, RollId, VaultItemId } from "./ids.js";
import { VaultItem, VaultItemState } from "./vault.js";

// ─────────────────────────────────────────────────────────────────────────────
// LOOP 3 — THE REWARD ENGINE. MOMENTS (nightly drops) · THE VAULT (claim) ·
// SILVERWARE HALL (cabinet) · Night Recap. AUTH (D-010): player bearer.
// The roll is SERVER-side and pre-reveal-logged (MOMENTS §3) — the client only
// reveals what the server already rolled.
// ─────────────────────────────────────────────────────────────────────────────

// The trigger catalogue (MOMENTS §2) — verified in-game acts that fire a drop.
export const MomentType = z.enum([
  "FIRST_WIN",
  "CLEAN_SHEET",
  "BACK_TO_BACK",
  "SCHEDULE_MASTER",
  "HOME_HERO",
]);
export type MomentType = z.infer<typeof MomentType>;

// The vehicle (the animated container, SILVERWARE) bound to each moment.
export const Vehicle = z.enum([
  "debut_trophy",
  "shutout_trophy",
  "back_to_back_chest", // D-R4 rename (was streak_chest)
  "bronze_chest",
  "rewards_pack",
]);
export type Vehicle = z.infer<typeof Vehicle>;

// What the payout roll produces (MOMENTS §3 + §3b pack contents).
export const PayoutType = z.enum([
  "ea_coins", // in-game coin voucher band
  "blg_coins", // instant, feeds the store economy
  "boost_2x", // #21+ only
  "trophy", // → cabinet (SILVERWARE HALL)
  "voucher", // airtime/coin voucher → THE VAULT (behind feature.voucher_rail)
]);
export type PayoutType = z.infer<typeof PayoutType>;

// ── Reveal (the client asks what the server already rolled) ───────────────────
// GET /moments/pending → the unrevealed drops waiting for this player (payout
// HIDDEN until reveal — the two-outcome suspense is the pack's personality).
export const PendingDrop = z.object({
  roll_id: RollId,
  night_id: NightId,
  moment: MomentType,
  vehicle: Vehicle,
  featured: z.boolean(),
  rolled_at: IsoTimestamp,
});
export type PendingDrop = z.infer<typeof PendingDrop>;

export const PendingDropsResponse = z.object({ drops: z.array(PendingDrop) });
export type PendingDropsResponse = z.infer<typeof PendingDropsResponse>;

// POST /moments/reveal { roll_id } → the payout (idempotent; sets revealed_at).
export const RevealRequest = z.object({ roll_id: RollId });
export type RevealRequest = z.infer<typeof RevealRequest>;

export const DropReveal = z.object({
  roll_id: RollId,
  moment: MomentType,
  vehicle: Vehicle,
  payout_type: PayoutType,
  payout_value: z.number().int().min(0), // coins amount / voucher minor / 0 for trophy+boost
  featured: z.boolean(),
  // For a vault-class payout (voucher): where it now lives (claim in THE VAULT).
  vault_item_id: VaultItemId.nullable(),
  // For a trophy payout: the cabinet piece it created.
  trophy_id: z.string().uuid().nullable(),
  revealed_at: IsoTimestamp,
});
export type DropReveal = z.infer<typeof DropReveal>;

// ── 2X Boost (MOMENTS §4 — the comeback spectacle, #21+ only) ────────────────
export const BoostState = z.object({
  armed: z.boolean(),
  night_id: NightId.nullable(),
  rank_at_arm: z.number().int().nullable(),
});
export type BoostState = z.infer<typeof BoostState>;

// ── THE VAULT (claim) ────────────────────────────────────────────────────────
// GET /vault → the player's vault items (claimables + in-flight + history).
export const VaultView = z.object({
  items: z.array(VaultItem),
  claimable_count: z.number().int().min(0),
  outstanding_value_minor: z.number().int().min(0),
});
export type VaultView = z.infer<typeof VaultView>;

// POST /vault/claim { vault_item_id } → the claim outcome. Idempotent (double-
// tap safe). A failed fulfilment resolves to CONVERTED (coins) or ESCALATED —
// never terminal-silent (THE VAULT §7).
export const VaultClaimRequest = z.object({ vault_item_id: VaultItemId });
export type VaultClaimRequest = z.infer<typeof VaultClaimRequest>;

export const VaultClaimResult = z.object({
  vault_item_id: VaultItemId,
  state: VaultItemState,
  // FULFILLED: the revealed code (voucher) or masked airtime confirmation.
  fulfilment_ref: z.string().nullable(),
  code: z.string().nullable(), // revealed ONLY on an ASSIGNED/FULFILLED code-pool voucher
  // CONVERTED: the coins granted in lieu (apology path).
  converted_coins: z.number().int().min(0).nullable(),
  message: z.string(), // player-facing status line
});
export type VaultClaimResult = z.infer<typeof VaultClaimResult>;

// ── SILVERWARE HALL — the Trophy Cabinet ─────────────────────────────────────
export const TrophyPiece = z.object({
  id: z.string().uuid(),
  trophy_type: z.string(), // 'debut' | 'shutout' | 'back_to_back' | 'schedule' | 'home_hero' | tournament cups
  name: z.string(),
  won_at: IsoTimestamp,
  match_id: MatchId.nullable(),
  edition_number: z.number().int().nullable(), // limited pieces: № n of total
  edition_total: z.number().int().nullable(),
  verified: z.boolean(), // VAR-backed (the engraving tick)
});
export type TrophyPiece = z.infer<typeof TrophyPiece>;

export const CabinetView = z.object({
  pieces: z.array(TrophyPiece),
  count: z.number().int().min(0),
});
export type CabinetView = z.infer<typeof CabinetView>;

// ── Night Recap v1 (MOMENTS §7 — replaces the streak-ignition beat) ──────────
export const NightRecap = z.object({
  night_code: z.string(),
  matches: z.number().int().min(0),
  wins: z.number().int().min(0),
  drops: z.array(
    z.object({ vehicle: Vehicle, payout_type: PayoutType, payout_value: z.number().int().min(0) }),
  ),
  rank_from: z.number().int().nullable(),
  rank_to: z.number().int().nullable(),
  boost_carried: z.boolean(), // an armed boost that expired at night close
  best_stat: z.string().nullable(), // "Best pass accuracy tonight: 89%" (loss nights end warm)
});
export type NightRecap = z.infer<typeof NightRecap>;

// ── DUGOUT — the payout/liability ops view (THE VAULT §5) ────────────────────
export const VaultLiabilityRow = z.object({
  pool_provider: z.string(),
  outstanding_items: z.number().int().min(0),
  outstanding_value_minor: z.number().int().min(0),
});
export type VaultLiabilityRow = z.infer<typeof VaultLiabilityRow>;

export const VaultOpsView = z.object({
  liability: z.array(VaultLiabilityRow),
  in_flight: z.array(VaultItem),
  escalated: z.array(VaultItem),
  total_outstanding_minor: z.number().int().min(0),
});
export type VaultOpsView = z.infer<typeof VaultOpsView>;

// Ops loads live voucher codes (DUGOUT S5 / THE VAULT §5): POST /dugout/voucher-batch.
export const LoadVoucherBatchRequest = z.object({
  pool_id: z.string().uuid(),
  codes: z.array(z.string().min(1)).min(1).max(5000),
  source_ref: z.string().nullable(),
});
export type LoadVoucherBatchRequest = z.infer<typeof LoadVoucherBatchRequest>;
